import { Download, ExternalLink } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import useGetInvoices, {
  type Invoice,
} from '@/services/api/billing/useGetInvoices'
import { formatBillingDate, formatMoney } from './format'

const STATUS_STYLE: Record<Invoice['status'], string> = {
  paid: 'bg-green-100 text-green-800',
  open: 'bg-yellow-100 text-yellow-800',
  uncollectible: 'bg-red-100 text-red-800',
  void: 'bg-gray-100 text-gray-600',
}

const STATUS_LABEL: Record<Invoice['status'], string> = {
  paid: 'Paid',
  open: 'Due',
  uncollectible: 'Unpaid',
  void: 'Void',
}

export default function InvoiceTable({
  teamId,
}: {
  teamId?: string | number | null
}) {
  const { data: invoices, isLoading, isError } = useGetInvoices({ teamId })

  return (
    <Card className="w-full min-w-0 p-6">
      <h3 className="mb-3 font-medium">Invoices</h3>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">
          Invoices could not be loaded.
        </p>
      ) : !invoices?.length ? (
        <p className="text-sm text-muted-foreground">No invoices yet.</p>
      ) : (
        <ul className="divide-y text-sm">
          {invoices.map((invoice, i) => (
            <li
              key={invoice.number ?? `${invoice.date}-${i}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2"
            >
              <span className="w-24 shrink-0 text-muted-foreground">
                {formatBillingDate(invoice.date)}
              </span>
              <span className="min-w-0 flex-1 truncate">
                {invoice.number ?? '—'}
              </span>
              <span className="tabular-nums">
                {formatMoney(invoice.amount, invoice.currency)}
              </span>
              <span
                className={cn(
                  'rounded px-2 py-0.5 text-xs font-medium',
                  STATUS_STYLE[invoice.status],
                )}
              >
                {STATUS_LABEL[invoice.status]}
              </span>
              <span className="flex gap-3">
                {invoice.hosted_invoice_url && (
                  <a
                    href={invoice.hosted_invoice_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {invoice.status === 'open' ? 'Pay' : 'View'}
                    <ExternalLink className="size-3" />
                  </a>
                )}
                {invoice.invoice_pdf && (
                  <a
                    href={invoice.invoice_pdf}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    PDF
                    <Download className="size-3" />
                  </a>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
